/* ============================================================
   Midnight Atelier — header chrome behaviour
   Vanilla JS, no dependencies. Handles:
   - sticky header condense on scroll
   - sticky mobile call/quote bar reveal
   - mobile drawer (open/close, focus trap, scroll lock, accordion)
   - mega menu (hover/focus/keyboard, closes on Esc/focus-out/mouseleave)
   Respects prefers-reduced-motion (CSS handles the actual easing;
   this file just avoids scheduling extra animation-dependent delays).
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var drawerOpen = false;

  /* ---------------------------------------------------------
     Sticky header condense
     --------------------------------------------------------- */
  var header = document.querySelector('[data-ma-header]');

  if (header) {
    var condenseTicking = false;
    var stickyMode = header.getAttribute('data-ma-sticky-mode') || 'scroll_up';
    var scrollUpEnabled = header.getAttribute('data-ma-sticky-scroll-up') !== 'false';
    var lastScrollY = window.scrollY || window.pageYOffset || 0;
    var SCROLL_HIDE_MIN = 200; // px scrolled before hide-on-down can kick in (stops hiding right near the top)
    var SCROLL_DELTA = 8; // px of movement required to register a direction change (jitter guard)

    /* megaPanel is assigned further down in this file (Mega menu section).
       By the time any scroll event actually fires the whole script has
       already run, so this closure always sees the assigned value. */
    var isMegaOpen = function () {
      return !!(megaPanel && !megaPanel.hidden);
    };

    var updateCondense = function () {
      condenseTicking = false;
      var currentScrollY = window.scrollY || window.pageYOffset || 0;

      if (currentScrollY > 120) {
        header.classList.add('is-condensed');
      } else {
        header.classList.remove('is-condensed');
      }

      if (stickyMode === 'scroll_up' && scrollUpEnabled) {
        if (currentScrollY < SCROLL_HIDE_MIN || drawerOpen || isMegaOpen() || header.contains(document.activeElement)) {
          header.classList.remove('is-hidden');
          lastScrollY = currentScrollY;
        } else {
          var delta = currentScrollY - lastScrollY;
          if (delta > SCROLL_DELTA) {
            header.classList.add('is-hidden');
            lastScrollY = currentScrollY;
          } else if (delta < -SCROLL_DELTA) {
            header.classList.remove('is-hidden');
            lastScrollY = currentScrollY;
          }
          /* else: movement is within the jitter guard — leave the
             hidden state and lastScrollY untouched so small back-and-
             forth scrolls accumulate instead of resetting the baseline. */
        }
      } else {
        lastScrollY = currentScrollY;
      }
    };

    window.addEventListener(
      'scroll',
      function () {
        if (!condenseTicking) {
          window.requestAnimationFrame(updateCondense);
          condenseTicking = true;
        }
      },
      { passive: true }
    );

    updateCondense();
  }

  /* ---------------------------------------------------------
     Sticky mobile call/quote bar
     --------------------------------------------------------- */
  var stickyBar = document.querySelector('[data-ma-stickybar]');
  var barTicking = false;

  var updateStickyBar = function () {
    barTicking = false;
    if (!stickyBar) return;
    if (drawerOpen) {
      stickyBar.classList.remove('is-visible');
      return;
    }
    if (window.scrollY > 200) {
      stickyBar.classList.add('is-visible');
    } else {
      stickyBar.classList.remove('is-visible');
    }
  };

  if (stickyBar) {
    window.addEventListener(
      'scroll',
      function () {
        if (!barTicking) {
          window.requestAnimationFrame(updateStickyBar);
          barTicking = true;
        }
      },
      { passive: true }
    );
    updateStickyBar();
  }

  /* ---------------------------------------------------------
     Shared focus-trap helper
     --------------------------------------------------------- */
  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getFocusable(container) {
    return Array.prototype.slice
      .call(container.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(function (el) {
        return el.offsetParent !== null;
      });
  }

  function trapKeydown(container, event) {
    if (event.key !== 'Tab') return;
    var focusable = getFocusable(container);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------------------------------------------------------
     Mobile drawer
     --------------------------------------------------------- */
  var drawer = document.querySelector('[data-ma-drawer]');
  var drawerTrigger = document.querySelector('[data-ma-drawer-trigger]');
  var drawerClose = document.querySelector('[data-ma-drawer-close]');
  var drawerScrim = document.querySelector('[data-ma-drawer-scrim]');
  var drawerPanel = drawer ? drawer.querySelector('[data-ma-drawer-panel]') : null;
  var lastFocusedBeforeDrawer = null;

  function onDrawerKeydown(event) {
    if (event.key === 'Escape') {
      closeDrawer();
      return;
    }
    if (drawerPanel) trapKeydown(drawerPanel, event);
  }

  function openDrawer() {
    if (!drawer) return;
    lastFocusedBeforeDrawer = document.activeElement;
    drawer.classList.add('is-open');
    document.documentElement.classList.add('ma-scroll-lock');
    if (drawerTrigger) drawerTrigger.setAttribute('aria-expanded', 'true');
    drawerOpen = true;
    updateStickyBar();
    document.addEventListener('keydown', onDrawerKeydown);

    window.setTimeout(
      function () {
        if (drawerClose) drawerClose.focus();
      },
      reduceMotion ? 0 : 220
    );
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('ma-scroll-lock');
    if (drawerTrigger) drawerTrigger.setAttribute('aria-expanded', 'false');
    drawerOpen = false;
    document.removeEventListener('keydown', onDrawerKeydown);
    updateStickyBar();
    if (lastFocusedBeforeDrawer && typeof lastFocusedBeforeDrawer.focus === 'function') {
      lastFocusedBeforeDrawer.focus();
    }
  }

  if (drawerTrigger && drawer) {
    drawerTrigger.addEventListener('click', function () {
      if (drawer.classList.contains('is-open')) {
        closeDrawer();
      } else {
        openDrawer();
      }
    });
  }
  if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
  if (drawerScrim) drawerScrim.addEventListener('click', closeDrawer);

  /* Drawer accordion (parent links with children) */
  var drawerToggles = Array.prototype.slice.call(document.querySelectorAll('[data-ma-drawer-toggle]'));
  drawerToggles.forEach(function (toggle) {
    toggle.addEventListener('click', function () {
      var expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      var submenuId = toggle.getAttribute('aria-controls');
      var submenu = submenuId ? document.getElementById(submenuId) : null;
      if (submenu) submenu.classList.toggle('is-open', !expanded);
    });
  });

  /* ---------------------------------------------------------
     Mega menu
     --------------------------------------------------------- */
  var megaTriggers = Array.prototype.slice.call(document.querySelectorAll('[data-ma-mega-trigger]'));
  var megaPanel = document.querySelector('[data-ma-mega-panel]');
  var megaCloseTimer = null;

  function openMega(trigger) {
    if (!megaPanel) return;
    window.clearTimeout(megaCloseTimer);
    megaPanel.removeAttribute('hidden');
    // Force layout so the opacity/transform transition runs.
    void megaPanel.offsetHeight;
    megaPanel.classList.add('is-open');
    megaTriggers.forEach(function (t) {
      t.setAttribute('aria-expanded', t === trigger ? 'true' : 'false');
    });
  }

  function closeMega() {
    if (!megaPanel) return;
    megaPanel.classList.remove('is-open');
    megaTriggers.forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
    });
    window.clearTimeout(megaCloseTimer);
    megaCloseTimer = window.setTimeout(
      function () {
        if (megaPanel && !megaPanel.classList.contains('is-open')) {
          megaPanel.setAttribute('hidden', '');
        }
      },
      reduceMotion ? 0 : 200
    );
  }

  if (megaTriggers.length && megaPanel && header) {
    megaTriggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        if (megaPanel.classList.contains('is-open')) {
          closeMega();
        } else {
          openMega(trigger);
        }
      });
      trigger.addEventListener('mouseenter', function () {
        openMega(trigger);
      });
      trigger.addEventListener('focus', function () {
        openMega(trigger);
      });
      trigger.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          closeMega();
          trigger.focus();
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          openMega(trigger);
          var firstLink = megaPanel.querySelector('a, button');
          if (firstLink) firstLink.focus();
        }
      });
    });

    header.addEventListener('mouseleave', function () {
      closeMega();
    });

    megaPanel.addEventListener('mouseenter', function () {
      window.clearTimeout(megaCloseTimer);
    });
    megaPanel.addEventListener('mouseleave', function () {
      closeMega();
    });
    megaPanel.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeMega();
        var openTrigger = megaTriggers[0];
        if (openTrigger) openTrigger.focus();
      }
    });

    document.addEventListener('focusin', function (event) {
      var withinPanel = megaPanel.contains(event.target);
      var onTrigger = megaTriggers.indexOf(event.target) !== -1;
      if (!withinPanel && !onTrigger) {
        closeMega();
      }
    });
  }
})();
